import { useMemo, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type { GuaranteedServiceSkuEntry, PatchGuaranteedServiceRequest } from '@nadoutong/contracts';
import { useT, type I18nKey } from '../../i18n/index.ts';
import { Button, ConfirmDialog, cx, Drawer, Input } from '../../ui/index.ts';
import { levelFlowCopy, sectionEffect } from './probeCopy.ts';
import { roundBudgetSeconds } from './probeEffects.ts';
import {
  changedFields,
  draftOf,
  rebaseDraft,
  draftValues,
  PROBE_SECTIONS,
  probeIssues,
  probePatch,
  probeValuesOf,
  restoreSectionDefaults,
  sectionIsDefault,
  specsOf,
  type ProbeDraft,
  type ProbeField,
  type ProbeFieldSpec,
  type ProbeIssue,
  type ProbeSection,
  type ProbeValues,
} from './probeSettingsModel.ts';
import { RowWriteStatus } from './RowWriteStatus.tsx';
import type { WriteOutcome } from './useGuaranteedWrites.ts';
import { isRowBusy, type RowWriteStatus as RowStatus } from './writeStatus.ts';

const SECTION_KEYS: Readonly<Record<ProbeSection, { readonly title: I18nKey; readonly hint: I18nKey }>> = {
  normal: { title: 'guaranteed.probe_settings.section.normal', hint: 'guaranteed.probe_settings.section.normal_hint' },
  quality: { title: 'guaranteed.probe_settings.section.quality', hint: 'guaranteed.probe_settings.section.quality_hint' },
  alert: { title: 'guaranteed.probe_settings.section.alert', hint: 'guaranteed.probe_settings.section.alert_hint' },
  war: { title: 'guaranteed.probe_settings.section.war', hint: 'guaranteed.probe_settings.section.war_hint' },
  cooldown: { title: 'guaranteed.probe_settings.section.cooldown', hint: 'guaranteed.probe_settings.section.cooldown_hint' },
};

const UNIT_KEYS: Readonly<Record<ProbeFieldSpec['unit'], I18nKey>> = {
  minutes: 'guaranteed.probe_settings.unit.minutes',
  seconds: 'guaranteed.probe_settings.unit.seconds',
  ms: 'guaranteed.probe_settings.unit.ms',
  shots: 'guaranteed.probe_settings.unit.shots',
  rounds: 'guaranteed.probe_settings.unit.rounds',
  pct: 'guaranteed.probe_settings.unit.pct',
  failures: 'guaranteed.probe_settings.unit.failures',
  waves: 'guaranteed.probe_settings.unit.waves',
  times: 'guaranteed.probe_settings.unit.times',
};

/** Longer explanations, on hover of the field label. */
const FIELD_TIPS: Partial<Record<ProbeField, I18nKey>> = {
  probe_shot_spacing_seconds: 'guaranteed.probe.timing_tip',
  probe_shot_timeout_seconds: 'guaranteed.probe.timing_tip',
  probe_confirm_after_seconds: 'guaranteed.probe.confirm_tip',
  probe_slow_ms: 'guaranteed.probe.slow_tip',
  probe_quality_trip_pct: 'guaranteed.probe.quality_tip',
  probe_quality_min_bad_shots: 'guaranteed.probe.quality_tip',
  probe_quality_recover_pct: 'guaranteed.probe.quality_tip',
  probe_quality_recover_window_minutes: 'guaranteed.probe.quality_tip',
  probe_alert_first_byte_late_ms: 'guaranteed.probe.first_byte_late_tip',
};

/** Signature of the stored values, so a refetch that changes them is noticed. */
function valuesKey(values: ProbeValues): string {
  return JSON.stringify(values);
}

function LevelBox({ name, cadence, tone }: {
  readonly name: string;
  readonly cadence: string;
  readonly tone: 'normal' | 'war' | 'alert';
}): JSX.Element {
  return (
    <span
      className={cx(
        'flex flex-col items-center rounded border px-2.5 py-1 text-center',
        tone === 'war' && 'border-bad-line bg-bad-dim/50 text-bad',
        tone === 'alert' && 'border-warn-line bg-warn-dim/50 text-warn',
        tone === 'normal' && 'border-ok-line bg-ok-dim/50 text-ok',
      )}
    >
      <span className="text-dense font-semibold">{name}</span>
      <span className="whitespace-nowrap font-mono text-micro text-paper-mid">{cadence}</span>
    </span>
  );
}

function FlowArrow({ caption, testId }: { readonly caption: string; readonly testId: string }): JSX.Element {
  return (
    <span className="flex min-w-0 flex-col gap-0.5 px-2" data-testid={testId}>
      <span className="text-center text-micro text-paper-mid">{caption}</span>
      <span aria-hidden="true" className="flex items-center text-paper-low">
        <span className="h-px flex-1 bg-line-strong" />
        <ArrowRight className="-ml-1 h-3.5 w-3.5 shrink-0" />
      </span>
    </span>
  );
}

/**
 * 常规 → 战争 → 警戒 → 常规 as three transitions, one per row, each with the
 * condition that takes it over the arrow (computed from the draft).
 */
function LevelFlow({ values }: { readonly values: ProbeValues }): JSX.Element {
  const t = useT();
  const copy = levelFlowCopy(values, t);
  const normal = <LevelBox name={t('guaranteed.probe_flow.normal')} cadence={copy.normalCadence} tone="normal" />;
  const war = <LevelBox name={t('guaranteed.probe_flow.war')} cadence={copy.warCadence} tone="war" />;
  const alert = <LevelBox name={t('guaranteed.probe_flow.alert')} cadence={copy.alertCadence} tone="alert" />;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line-soft bg-well/40 p-3" data-testid="guaranteed-probe-flow">
      <div className="grid grid-cols-[88px_minmax(0,1fr)_88px] items-center gap-y-2">
        {normal}
        <FlowArrow caption={copy.toWar} testId="guaranteed-probe-flow-to-war" />
        {war}
        {war}
        <FlowArrow caption={copy.toAlert} testId="guaranteed-probe-flow-to-alert" />
        {alert}
        {alert}
        <FlowArrow caption={copy.toNormal} testId="guaranteed-probe-flow-to-normal" />
        {normal}
      </div>
      <p className="m-0 text-micro text-paper-low" data-testid="guaranteed-probe-flow-cooldown">{copy.cooldown}</p>
    </div>
  );
}

function FieldInput({ entry, text, stored, issue, disabled, budget, onChange }: {
  readonly entry: ProbeFieldSpec;
  /** Round budget of the draft, for the timing tips. */
  readonly budget: number;
  readonly text: string;
  readonly stored: number;
  readonly issue: ProbeIssue | undefined;
  readonly disabled: boolean;
  readonly onChange: (text: string) => void;
}): JSX.Element {
  const t = useT();
  const label = t(entry.label);
  const edited = text.trim() !== String(stored);
  const custom = text.trim() !== String(entry.defaultValue);
  const tipKey = FIELD_TIPS[entry.field];
  return (
    <label
      className="flex min-w-0 flex-col gap-1"
      title={tipKey === undefined ? undefined : t(tipKey, { budget })}
      data-edited={edited || undefined}
      data-custom={custom || undefined}
    >
      <span className={cx('text-label', edited ? 'text-signal' : 'text-paper-mid')}>{label}</span>
      <span className="flex items-center gap-1.5">
        <Input
          wrapperClassName="w-24"
          className={cx('text-right', edited && issue === undefined && 'border-signal-deep', custom && !edited && 'text-signal')}
          mono
          value={text}
          disabled={disabled}
          inputMode="numeric"
          aria-label={label}
          invalid={issue !== undefined}
          data-testid={`guaranteed-probe-setting-${entry.field}`}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
        <span className="text-micro text-paper-low">{t(UNIT_KEYS[entry.unit])}</span>
      </span>
      {issue === undefined ? (
        <span className="text-micro text-paper-low">
          {t('guaranteed.probe_settings.default', { value: entry.defaultValue })}
        </span>
      ) : (
        <span role="alert" className="text-micro text-bad" data-testid={`guaranteed-probe-setting-error-${entry.field}`}>
          {t(issue.key, issue.params)}
        </span>
      )}
    </label>
  );
}

function SectionCard({ section, draft, baseline, issues, busy, onField, onRestore }: {
  readonly section: ProbeSection;
  readonly draft: ProbeDraft;
  readonly baseline: ProbeValues;
  readonly issues: ReadonlyMap<ProbeField, ProbeIssue>;
  readonly busy: boolean;
  readonly onField: (field: ProbeField, text: string) => void;
  readonly onRestore: () => void;
}): JSX.Element {
  const t = useT();
  const keys = SECTION_KEYS[section];
  const values = draftValues(draft, baseline);
  const sectionHasIssue = specsOf(section).some((entry) => issues.has(entry.field));
  return (
    <section className="flex flex-col gap-2.5 rounded-lg border border-line-soft p-3" data-testid={`guaranteed-probe-section-${section}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3 className="m-0 text-section text-paper">{t(keys.title)}</h3>
          <p className="m-0 text-label text-paper-low">{t(keys.hint)}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={busy || sectionIsDefault(draft, section)}
          onClick={onRestore}
          data-testid={`guaranteed-probe-restore-${section}`}
        >
          {t('guaranteed.probe_settings.restore')}
        </Button>
      </div>
      <p
        className={cx('m-0 rounded bg-raised px-2 py-1 text-label', sectionHasIssue ? 'text-paper-low' : 'text-paper-mid')}
        data-testid={`guaranteed-probe-effect-${section}`}
      >
        {sectionEffect(section, values, t)}
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        {specsOf(section).map((entry) => (
          <FieldInput
            key={entry.field}
            entry={entry}
            text={draft[entry.field]}
            stored={baseline[entry.field]}
            issue={issues.get(entry.field)}
            disabled={busy}
            budget={roundBudgetSeconds(values)}
            onChange={(text) => onField(entry.field, text)}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * 「<SKU> · 探测设置」 (#2658 follow-up): the level flow on top, then one card
 * per level with its computed effect, and one save that PATCHes only the
 * fields that differ from the stored row. Closing with unsaved edits asks
 * first. The row's write state (saving → saved / error) shows at the bottom.
 */
export function ProbeSettingsDrawer({ row, status, probesOff, onClose, onPatch }: {
  readonly row: GuaranteedServiceSkuEntry;
  readonly status: RowStatus | undefined;
  readonly probesOff: boolean;
  readonly onClose: () => void;
  readonly onPatch: (body: PatchGuaranteedServiceRequest) => Promise<WriteOutcome>;
}): JSX.Element {
  const t = useT();
  const stored = probeValuesOf(row);
  const storedKey = valuesKey(stored);
  const [baseline, setBaseline] = useState<ProbeValues>(stored);
  const [draft, setDraft] = useState<ProbeDraft>(() => draftOf(stored));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const busy = isRowBusy(status);
  const changed = changedFields(draft, baseline);
  const dirty = changed.length > 0;
  const issues = useMemo(() => probeIssues(draft, baseline), [draft, baseline]);

  // A refetch with new stored values replaces the baseline and rebases the
  // draft: fields the operator has not touched take the new values, their
  // own edits stay (derived state, adjusted during render).
  const [seenKey, setSeenKey] = useState(storedKey);
  if (seenKey !== storedKey) {
    setSeenKey(storedKey);
    setBaseline(stored);
    setDraft(rebaseDraft(draft, baseline, stored));
  }

  function requestClose(): void {
    if (dirty && !busy) {
      setConfirmOpen(true);
      return;
    }
    onClose();
  }

  async function save(): Promise<void> {
    if (!dirty || issues.size > 0 || busy) return;
    const body = probePatch(draft, baseline);
    const outcome = await onPatch(body);
    if (outcome.ok) setBaseline(draftValues(draft, baseline));
  }

  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open) requestClose();
      }}
      title={t('guaranteed.probe_settings.title', { sku: row.sku_name })}
      description={t('guaranteed.probe_settings.description')}
      width="lg"
      closeLabel={t('guaranteed.probe_settings.close')}
      testId={`guaranteed-probe-settings-${row.sku_id}`}
      footer={(
        <div className="flex w-full flex-col gap-2">
          <RowWriteStatus status={status} testId={`guaranteed-probe-settings-status-${row.sku_id}`} />
          <div className="flex items-center justify-end gap-2">
            <span className="mr-auto text-label text-paper-low" data-testid="guaranteed-probe-settings-changed">
              {dirty
                ? t('guaranteed.probe_settings.changed', { n: changed.length })
                : t('guaranteed.probe_settings.unchanged')}
            </span>
            <Button variant="ghost" onClick={requestClose} disabled={busy} data-testid="guaranteed-probe-settings-close">
              {t('guaranteed.probe_settings.close')}
            </Button>
            <Button
              variant="primary"
              onClick={() => void save()}
              disabled={!dirty || issues.size > 0 || busy}
              loading={busy}
              data-testid="guaranteed-probe-settings-save"
            >
              {t('guaranteed.probe_settings.save')}
            </Button>
          </div>
        </div>
      )}
    >
      <div className="flex flex-col gap-3">
        {probesOff ? (
          <p className="m-0 rounded border border-line-soft bg-well/40 px-2.5 py-1.5 text-label text-paper-mid">
            {t('guaranteed.probe_settings.probes_off')}
          </p>
        ) : null}
        <LevelFlow values={draftValues(draft, baseline)} />
        {PROBE_SECTIONS.map((section) => (
          <SectionCard
            key={section}
            section={section}
            draft={draft}
            baseline={baseline}
            issues={issues}
            busy={busy}
            onField={(field, text) => setDraft((current) => ({ ...current, [field]: text }))}
            onRestore={() => setDraft((current) => restoreSectionDefaults(current, section))}
          />
        ))}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('guaranteed.probe_settings.discard_title')}
        body={t('guaranteed.probe_settings.discard_body', { n: changed.length })}
        confirmLabel={t('guaranteed.probe_settings.discard_confirm')}
        cancelLabel={t('guaranteed.probe_settings.discard_cancel')}
        closeLabel={t('guaranteed.probe_settings.close')}
        tone="danger"
        testId="guaranteed-probe-settings-discard"
        onConfirm={() => {
          setConfirmOpen(false);
          onClose();
        }}
      />
    </Drawer>
  );
}
